import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IMAGE_LIST } from '@app-core/constants/constants';
import { CLIENT_CONFIG } from '@config/config';
import { ErrorStateMatcher } from '@angular/material/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import {
  DRIVERID_INPUT_PATTERN,
  DriverType,
  IMG_PLACEHOLDER_COUNT,
  IMG_PLACEHOLDER_URL,
  ManageAction,
  TOTAL_UPLOAD_COUNT,
} from '@app-driver-management/common/driver-management.constants';
import { DriverManagementService } from '@app-driver-management/services/driver-management.service';
import { distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DriverEnrollmentComponent } from '../driver-enrollment/driver-enrollment.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { asymmetricArrayDiff } from '@app-core/models/dirty-check';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { DriverImageUploadService } from '@app-driver-management/services/driver-image-upload.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { DeleteDriverResponse, Driver, DriverData, DriverUpdateResponse } from '@app-driver-management/common/driver-management.model';
import { ValidatorService } from '@app-shared/services/validator/validator.service';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  public isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return control && control.invalid && (control.dirty || control.touched || isSubmitted);
  }
}

interface QueryParams {
  action: ManageAction;
  driverType?: string;
  driverId?: string;
  email?: string;
  driverName?: string;
}

@Component({
  selector: 'app-edit-driver',
  templateUrl: './edit-driver.component.html',
  styleUrls: ['./edit-driver.component.scss'],
})
export class EditDriverComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('imageUpload')
  private imageUpload: ElementRef;

  public tagList: Number[] = [];
  public oldDriverTags: Number[] = [];
  private generatedSampleIds = [];
  private uploadedFiles = [];
  private validSamples = [];
  private invalidSampleIncides = [];
  private conflictingPersonId = '';
  private driverId: string;

  public email: string;
  public driverName: string;
  public driverDetails: Driver;
  public actionName: ManageAction = ManageAction.Add;
  public form: FormGroup;
  public driverTypeEnum = DriverType;
  public clientConfig = CLIENT_CONFIG;
  public matcher = new MyErrorStateMatcher();
  public tagSelected: boolean = false;
  public driverTags: Number[] = [];
  public loader: boolean = false;
  public driverDetailsLoader: boolean = false;
  public updateTagsLoader: boolean = false;
  public isAddDriverSuccessful: boolean;
  public driverImages = [];
  public isReplaceMode: boolean = false;
  public invalidSamples = [];
  public showLoader: boolean = false;
  public showSuccess: boolean = false;
  public showWarning: boolean = false;
  public totalUploadCount: number = TOTAL_UPLOAD_COUNT;
  public showError: boolean = false;
  public duplicatePersonError: boolean = false;
  public conflictingDriverId: string = '';
  public conflictPersonError: boolean = false;
  public conflictSamples = [];
  public imageList = IMAGE_LIST;
  public errorMessage: string;
  public disableSubmitButton: boolean = true;
  public driverType: string;
  public isFaceIdEnabled: boolean = false;
  public isFaceIdEnabled$ = new BehaviorSubject<boolean>(false);
  public imageUrl = IMG_PLACEHOLDER_URL;
  public repeatCount = IMG_PLACEHOLDER_COUNT;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public disable: boolean;
  private initialValues: object;
  public isImageUpdating: boolean = false;
  public isEditDriverSuccessful: boolean;
  public sourceKey: string;
  ManageAction = ManageAction;
  // assigned coach
  public coachId: string;
  public coachSelected: boolean = false;
  public assignCoachValue: string;
  public isCoachAssign: boolean = false;
  constructor(
    private driverManagementService: DriverManagementService,
    private route: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private snackBarService: SnackBarService,
    private activatedRoute: ActivatedRoute,
    private imageHandlerService: DriverImageUploadService,
    private validatorService: ValidatorService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: QueryParams) => {
      this.actionName = params.action;
      this.driverId = params.driverId || '';
    });

    this.isFaceIdEnabled$.next(this.activatedRoute.snapshot.data['faceIdEnabled']);
    this.isFaceIdEnabled$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isFaceIdEnabled) => {
      this.isFaceIdEnabled = isFaceIdEnabled;
    });

    if (this.actionName === ManageAction.Edit) {
      this.getdriverDetails(this.driverId);
    }
    this.generateform();
    this.cdr.detectChanges();
  }

  public ngAfterViewInit(): void {
    this.cdr.detectChanges();
    this.form.valueChanges.pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))).subscribe(() => {
      const formValue = JSON.stringify(this.form.getRawValue());
      const initialValue = JSON.stringify(this.initialValues);
      const isSame = initialValue === formValue;
      this.disableSubmitButton = isSame || this.form.invalid;
    });
  }

  private generateform(): void {
    this.form = this.fb.group({
      driverEmail: new FormControl(
        {
          value: this.driverDetails?.email,
          disabled: false,
        },
        [this.validatorService.customEmailValidator()]
      ),
      driverId: new FormControl(
        { value: this.driverDetails?.driverId, disabled: !!this.driverDetails?.driverId || this.actionName === ManageAction.Edit },
        [Validators.required, Validators.maxLength(48), Validators.pattern(DRIVERID_INPUT_PATTERN)]
      ),
      driverName: new FormControl(
        {
          value: this.driverDetails?.driverName,
          disabled: false,
        },
        [Validators.required, Validators.pattern(`^[a-zA-Z]+(?: [a-zA-Z]+){0,31}$`)]
      ),
      tagIds: new FormControl({
        value: [],
        disabled: false,
      }),
    });
    this.initialValues = this.form.getRawValue();
  }

  public async chooseImages(event): Promise<void> {
    if (!this.invalidSampleIncides.length) {
      this.driverImages = [];
    }
    this.showError = false;
    this.uploadedFiles = event.target.files;

    const validation = this.imageHandlerService.validateFiles(this.uploadedFiles, this.totalUploadCount);
    if (!validation.isValid) {
      if (!this.isReplaceMode) {
        this.resetDriverEnrollment();
      }
      this.showWarning = validation.warning;
      return;
    }

    this.showWarning = false;
    if (!this.form.invalid) {
      this.disableSubmitButton = false;
    }

    try {
      const dataUrls = await this.imageHandlerService.readFilesAsDataURL(this.uploadedFiles);

      // Step 3: Process images
      this.driverImages = this.imageHandlerService.processImages(
        dataUrls,
        this.isReplaceMode,
        this.invalidSampleIncides,
        this.driverImages
      );
      this.isImageUpdating = true;
      this.disable = false;
    } catch (error) {
      this.showError = true;
    }
  }

  public uploadFilesToS3(sampleIdsList, fileList): void {
    this.showLoader = true;
    this.loader = true;

    this.imageHandlerService.uploadFilesToS3(sampleIdsList, fileList).subscribe(
      () => {
        this.enrollDriver(sampleIdsList);
      },
      () => {
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
      }
    );
  }

  private enrollDriver(sampleList): void {
    this.showLoader = true;
    this.disable = false;
    const driverId = this.driverId || this.form.get('driverId').value;
    const driverName = this.driverDetails?.driverName || this.form.get('driverName').value;
    const personId = this.driverDetails?.appMetadata?.personId || null;
    const driverEmail = this.driverDetails?.email || '';
    const body = {
      sampleIds: sampleList.map((x) => x.sampleId),
      metadata: {
        driverId,
        driverName,
        driverEmail,
      },
      personId,
      ...(this.isReplaceMode ? { validSampleIds: this.validSamples.length ? this.validSamples.map((x) => x.sampleId) : undefined } : {}),
    };

    this.invalidSampleIncides = [];
    this.driverManagementService.enrollDriver(body).subscribe(
      (res) => {
        this.invalidSamples = [];
        this.driverImages = this.driverImages.map((img) => {
          return {
            ...img,
            isValid: true,
          };
        });

        if (personId) {
          this.showSuccess = true;
          this.snackBarService.success(this.translate.instant('driverEnrollmentDriverImageModified'));
          this.disableSubmitButton = true;
          this.navigateBack();
        }
        // Update driver call
        if (res && !personId) {
          this.showLoader = true;
          this.updateDriverMetadataWithPersonId(res.body.personId).subscribe(
            () => {
              this.showSuccess = true;
              this.snackBarService.success(this.translate.instant('driverEnrollmentSuccessfullyEnolled'));
              this.disableSubmitButton = true;
              this.navigateBack();
            },
            () => {
              this.showError = true;
              this.disableSubmitButton = true;
              this.resetDriverEnrollment();
              this.deletePersonDetails([res.body.personId || personId]).subscribe();
            }
          );
        }
        this.showLoader = false;
        this.loader = false;
        this.disableSubmitButton = true;
      },
      (err) => {
        const data = err.error;
        if (err && err.status === 409) {
          this.verifyConflictPersonDetails(data);
          this.disable = true;
          return;
        }

        this.isReplaceMode = true;
        this.invalidSampleIncides = [];
        if (err && err.status === 400) {
          this.validSamples = data.validSamples.map((sample) => {
            return {
              sampleId: sample,
              isValid: true,
            };
          });
          this.invalidSamples = data.invalidSamples.map((sample) => {
            return {
              ...sample,
              isValid: false,
            };
          });

          const allSamples = [...this.validSamples, ...this.invalidSamples];

          // assigning valid state for each image
          this.driverImages.map((img, index) => {
            allSamples.map((sample) => {
              if (img.sampleId === sample.sampleId) {
                this.driverImages[index].isValid = sample.isValid;
              }
            });
          });

          // getting invalid image indices
          this.driverImages.map((img, index) => {
            if (img.isValid === false) {
              this.invalidSampleIncides.push(index);
            }
          });

          this.totalUploadCount = this.invalidSamples.length;
          this.showLoader = false;
          this.loader = false;
          this.disableSubmitButton = true;
          return;
        }
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
        this.duplicatePersonError = false;
        this.disableSubmitButton = true;
        this.resetDriverEnrollment();
      }
    );
  }

  private resetDriverEnrollment(): void {
    this.totalUploadCount = 5;
    this.validSamples = [];
    this.invalidSampleIncides = [];
    this.invalidSamples = [];
    this.showLoader = false;
    this.loader = false;
    this.showWarning = false;
    this.showSuccess = false;
    this.isReplaceMode = false;
    this.conflictingPersonId = '';
    this.conflictingDriverId = '';
    this.conflictPersonError = false;
    this.conflictSamples = [];
    this.driverImages = [];
    this.uploadedFiles = [];
    this.generatedSampleIds = [];
    if (this.imageUpload) {
      this.imageUpload.nativeElement.value = '';
    }
  }

  public onAcceptConflict(): void {
    this.updateDriverMetadataWithPersonId(this.conflictingPersonId).subscribe(
      () => {
        this.snackBarService.success(this.translate.instant('driverEnrollmentSuccessfullyEnolled'));
        this.showSuccess = true;
        this.showError = false;
        this.loader = false;
        this.disableSubmitButton = true;
        this.navigateBack();
      },
      () => {
        this.showError = true;
        this.loader = false;
        this.resetDriverEnrollment();
        this.disableSubmitButton = true;
        this.deletePersonDetails([this.conflictingPersonId]).subscribe();
      }
    );
  }

  public onRejectConflict(): void {
    this.showLoader = true;
    this.deletePersonDetails([this.conflictingPersonId])
      .pipe(finalize(() => (this.showLoader = false)))
      .subscribe(
        () => {
          this.loader = false;
          this.showError = false;
          this.resetDriverEnrollment();
          this.disableSubmitButton = true;
          this.navigateBack();
        },
        () => {
          this.showError = true;
          this.loader = false;
          this.disableSubmitButton = true;
          this.resetDriverEnrollment();
        }
      );
  }

  private verifyConflictPersonDetails(data: { message: string }): void {
    // data.message format: "Person is already enrolled with personId- dbb4f03c-44e7-4c29-92f4-897342f0a60b"
    this.showLoader = true;
    const [, conflictingPersonId] = (data.message || '').split('- '); // space is required after hyphen
    this.conflictingPersonId = conflictingPersonId;
    if (!conflictingPersonId) {
      this.duplicatePersonError = true;
      this.resetDriverEnrollment();
      this.disableSubmitButton = true;
      return;
    }
    const params = { personId: conflictingPersonId };
    this.driverManagementService
      .getDriverImages(params)
      .pipe(
        finalize(() => {
          this.showLoader = false;
          this.loader = false;
          this.disableSubmitButton = true;
        })
      )
      .subscribe(
        (personDetails) => {
          const { metadata = {}, samples = [] } = personDetails;
          this.showError = true;
          if (this.form.get('driverId').value === metadata.driverId) {
            this.conflictPersonError = true;
            this.conflictSamples = samples;
            this.duplicatePersonError = true;
            return;
          }
          this.duplicatePersonError = true;
          this.resetDriverEnrollment();
          this.disableSubmitButton = true;
          this.conflictingDriverId = metadata?.driverId;
        },
        () => {
          this.showError = true;
          this.duplicatePersonError = true;
          this.disableSubmitButton = true;
          this.resetDriverEnrollment();
        }
      );
  }

  private updateDriverMetadataWithPersonId(personId: string): Observable<any> {
    const { driverId = this.form.get('driverId').value, email = this.form.get('driverEmail').value } = this.driverDetails || {};
    const updateDriverBody = {
      driverId: this.form.get('driverId').value,
      driverName: this.form.get('driverName').value,
      username: email?.length > 0 ? email : driverId,
      ...(this.tagList && this.tagList.length > 0 ? { tagIds: this.tagList } : {}),
      ...(email ? { email: email } : { email: this.driverDetails?.email }),
      personId: personId,
      source: this.driverDetails?.source || 'REGISTERED_DRIVER',
    };
    this.showSuccess = false;
    this.showLoader = true;
    return this.driverManagementService.editDriver(updateDriverBody).pipe(
      finalize(() => {
        this.showLoader = false;
      })
    );
  }

  private deletePersonDetails(personIds): Observable<DeleteDriverResponse> {
    const param = {
      personIds,
    };
    return this.driverManagementService.deletePersonDetails(param);
  }

  public onSubmit(): void {
    if (this.actionName === ManageAction.Add && !this.isAddDriverSuccessful) {
      this.addDriverDetails();
      this.mixpanelService.track('Save Details - Add Driver / Driver Page');
    } else {
      this.mixpanelService.track('Save Details - Edit Driver / Driver Page');
      this.editDriverDetails();
    }
  }

  public editDriverDetails() {
    const formEmail = this.form.get('driverEmail').value;
    const params = {
      driverId: this.driverDetails?.driverId,
      driverName: this.form.get('driverName').value,
      username: this.driverDetails?.email ? this.driverDetails?.email : {},
      ...(this.tagList && this.tagList.length > 0 ? { tagIds: this.tagList } : { tagIds: [] }),
      ...(formEmail ? { email: formEmail } : { email: this.driverDetails?.email }),
      ...(this.coachId ? { coachId: this.coachId } : {}),
      source: this.driverDetails?.source,
    };
    const isDriverNameChanged = this.form.get('driverName').dirty;
    const isEmailChanged = this.form.get('driverEmail').dirty;
    if (isDriverNameChanged || this.tagSelected || isEmailChanged || this.coachSelected) {
      if (!this.isImageUpdating) {
        // Condition 1: Only name or tags are edited
        this.updateDriverDetails(params);
      } else {
        // Condition 3: Name/tags and images are edited
        this.loader = true;
        this.errorMessage = null;
        this.driverManagementService
          .editDriver(params)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(
            (res: DriverUpdateResponse) => {
              if (this.driverImages.length) {
                if (res?.statusCode == 200) {
                  this.isEditDriverSuccessful = true;
                } else if (res?.statusCode == 202) {
                  this.isCoachAssign = true;
                }
                this.showLoader = true;
                this.addDriverImages();
              } else {
                if (res?.statusCode == 200) {
                  this.snackBarService.success(this.translate.instant('driverManagementUpdateSuccessfully'));
                } else if (res?.statusCode == 202) {
                  this.snackBarService.success(this.translate.instant('unableToAssignCoach'));
                }
                this.loader = false;
                this.navigateBack();
              }
            },
            (err) => {
              this.loader = false;
              this.showLoader = false;
              this.isAddDriverSuccessful = false;
              this.disableSubmitButton = true;
              if (err === 'No internet') {
                // Handle no internet case
                this.errorMessage = this.translate.instant('NetworkIssue');
                return;
              }
              if (err.status === 409 && err.error.errCode === 'DRIVER_ID_EXIST') {
                this.errorMessage = this.translate.instant('driverManagementDriverIdExists');
              } else if (err.status === 409 && err.error.errCode === 'EMAIL_ID_EXIST') {
                this.errorMessage = this.translate.instant('driverManagementEmailExists');
              } else {
                this.errorMessage = this.translate.instant('driverManagementDriverUpdateFailed');
              }
            }
          );
      }
    } else if (this.driverImages.length) {
      // Condition 2: Only images are edited
      this.UpdateDriverImages();
    } else {
      return;
    }
  }
  public updateDriverDetails(params) {
    this.loader = true;
    this.driverManagementService
      .editDriver(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: DriverUpdateResponse) => {
          if (res?.statusCode == 200) {
            this.snackBarService.success(this.translate.instant('driverManagementUpdateSuccessfully'));
          } else if (res?.statusCode == 202) {
            this.snackBarService.success(this.translate.instant('unableToAssignCoach'));
          }
          this.loader = false;
          this.navigateBack();
        },
        (err) => {
          this.loader = false;
          this.showLoader = false;
          this.isAddDriverSuccessful = false;
          this.disableSubmitButton = true;
          if (err === 'No internet') {
            // Handle no internet case
            this.errorMessage = this.translate.instant('NetworkIssue');
            return;
          }
          if (err.status === 409 && err.error.errCode === 'DRIVER_ID_EXIST') {
            this.errorMessage = this.translate.instant('driverManagementDriverIdExists');
          } else if (err.status === 409 && err.error.errCode === 'EMAIL_ID_EXIST') {
            this.errorMessage = this.translate.instant('driverManagementEmailExists');
          } else {
            this.errorMessage = this.translate.instant('driverManagementDriverUpdateFailed');
          }
        }
      );
  }
  public UpdateDriverImages() {
    this.loader = true;
    this.showLoader = true;
    this.addDriverImages();
  }
  public viewGuide(): void {
    this.dialog.open(DriverEnrollmentComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      maxWidth: '640px',
      autoFocus: false,
      position: { top: '24px' },
    });
  }

  public selectedTags(tags: Number[]): void {
    this.form.get('tagIds').patchValue(tags.sort((a: any, b: any) => a - b));
    this.tagList = tags;
    const eventTagsIds: Number[] = (this.oldDriverTags || []).map((t: any) => t.tagId);
    const diff = asymmetricArrayDiff<Number>(tags || [], eventTagsIds);
    this.tagSelected = Boolean(diff.length);
  }

  public getDriverTags() {
    this.driverManagementService
      .getDriverTags_v2(this.driverId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res) => {
          this.driverDetails = res.data;
          this.driverTags = res.data?.driverTags;
          this.oldDriverTags = res?.data?.driverTags;
          this.updateFormDetails();
        },
        () => {}
      );
  }
  private getdriverDetails(driverId) {
    this.driverManagementService
      .getdriverDetails(driverId)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: DriverData) => {
          const { data } = res;
          this.driverDetails = data;
          const { assignedCoach, appMetadata } = data || {};
          const { coachAssigned } = assignedCoach || {};
          this.assignCoachValue = coachAssigned?.coachName;
          if (appMetadata?.personId) {
            this.getRegisterDriverImages(appMetadata.personId);
          }
          this.updateFormDetails();
        },
        () => {}
      );
  }

  private updateFormDetails() {
    this.oldDriverTags = this.driverDetails?.driverTags;
    // setting null here to ensure proper comparision between initial value of driver and driver edit form
    const { email = null, driverId = null, driverName = null, driverTags = [] } = this.driverDetails;
    const driverDetails = {
      driverEmail: email,
      driverId: driverId,
      driverName: driverName,
      tagIds: driverTags?.map((t) => t.tagId)?.sort((a, b) => a - b),
    };
    this.initialValues = driverDetails;
    this.form.reset(driverDetails);
    if (this.driverDetails?.email) {
      this.form.get('driverEmail')?.disable();
    }
  }

  private addDriverDetails(): void {
    this.loader = true;
    this.errorMessage = null;
    const params = {
      driverId: this.form.get('driverId').value,
      driverName: this.form.get('driverName').value,
      ...(this.form.get('driverEmail').value !== '' ? { email: this.form.get('driverEmail').value } : {}),
      ...(this.coachId ? { coachId: this.coachId } : {}),
      ...(this.tagList && this.tagList.length > 0 ? { tagIds: this.tagList } : {}),
    };
    this.sourceKey = this.form.get('driverEmail').value ? 'REGISTERED_DRIVER' : 'REGISTERED_NO_APP_DRIVER';
    this.driverManagementService
      .registerDriver([params], this.sourceKey)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          if (this.driverImages.length) {
            this.isAddDriverSuccessful = true;
            this.showLoader = true;
            this.addDriverImages();
          } else {
            this.snackBarService.success(this.translate.instant('driverManagementAddedSuccessfully'));
            this.loader = false;
            this.navigateBack();
          }
        },
        (err) => {
          this.loader = false;
          this.showLoader = false;
          this.isAddDriverSuccessful = false;
          this.disableSubmitButton = true;
          if (err === 'No internet') {
            // Handle no internet case
            this.errorMessage = this.translate.instant('FailedToAddDriverDueToNetwork');
            return;
          }
          if (err.status === 409 && err.error.errCode === 'DRIVER_ID_EXIST') {
            this.errorMessage = this.translate.instant('driverManagementDriverIdExists');
          } else if (err.status === 409 && err.error.errCode === 'EMAIL_ID_EXIST') {
            this.errorMessage = this.translate.instant('driverManagementEmailExists');
          } else if (err.status === 409 && err.error.errCode === 'DRIVER_EXIST_AS_INSTALLER') {
            this.errorMessage = this.translate.instant('driverExistAsInstaller');
          } else {
            this.errorMessage = this.translate.instant('driverManagementSignUpFailed');
          }
        }
      );
  }

  private addDriverImages(): void {
    const fileList = Object.keys(this.uploadedFiles).map((key) => this.uploadedFiles[key]);

    this.showLoader = true;
    this.driverManagementService.generateSampleUrls().subscribe(
      ({ samples = [] }) => {
        if (this.isReplaceMode) {
          this.generatedSampleIds = this.generatedSampleIds.map((x, idx) => {
            const invalidSampleIds = this.invalidSamples.map((y) => y.sampleId);
            if (invalidSampleIds.includes(x)) {
              return samples[idx].sampleId;
            }
            return x;
          });
        } else {
          this.generatedSampleIds = samples.map((x: any) => x.sampleId);
        }
        const sampleList = this.isReplaceMode ? samples.slice(0, this.totalUploadCount) : samples;
        this.uploadFilesToS3(sampleList, fileList);
        this.driverImages = this.driverImages.map((x, idx) => {
          return {
            ...x,
            sampleId: this.generatedSampleIds[idx],
          };
        });
      },
      (err) => {
        this.loader = false;
        this.showLoader = false;
        this.showError = true;
        if (err === 'No internet') {
          // Handle no internet case
          this.errorMessage = this.translate.instant('NetworkIssue');
          return;
        }
      }
    );
  }

  public navigateBack(): void {
    this.location.back();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getRegisterDriverImages(personId) {
    const params = {
      personId: personId,
    };
    this.loader = true;
    this.driverManagementService
      .getDriverImages(params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res) => {
          this.driverImages = res.samples;
        },
        () => {
          this.driverImages = [];
        }
      );
  }
  public onCoachSelected(coachId: string) {
    this.coachId = coachId;
    this.coachSelected = true;
    if (!this.form.invalid) {
      this.disableSubmitButton = false;
    }
  }
}
